import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { yandexSetPasswordChanged } from 'services/yandex';
import { useDispatch, useSelector } from 'store';
import {
	fetchCompanyRights,
	refreshCompanyData,
	selectActiveCompanyId,
	selectCompanyProductId,
	selectCompanyProductSubclient,
	selectHasCompanyRights,
} from 'store/companiesReducer';
import { fetchProduct, selectActiveProduct } from 'store/productsReducer';
import { getRemainingDays } from 'utils/date';
import { formateNumberQuantityText } from 'utils/formatter';
import Button from 'components/Button';
import Card from 'components/Card';
import ModalConfirm from 'components/Modals/ModalConfirm';
import { ReactComponent as ArrowIcon } from 'assets/imgs/arrow-left.svg';
import { ReactComponent as WarningIcon } from 'assets/imgs/warning.svg';
import classes from './advertisingCampaignsLayout.module.scss';

const LIMIT_DAYS_TO_CHANGE_TEMPORARY_PASSWORD = 30;

const AdvertisingCampaignsLayout: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { productId = '', campaignId = '' } = useParams();
	const activeProduct = useSelector(selectActiveProduct);
	const activeCompanyId = useSelector(selectActiveCompanyId);
	const hasCompanyRights = useSelector(selectHasCompanyRights);
	const companySubclientData = useSelector(selectCompanyProductSubclient);
	const companyProductId = useSelector(selectCompanyProductId);
	const [isLoading, setIsLoading] = useState(true);
	const [isSetPasswordChangedLoading, setIsSetPasswordChangedLoading] = useState(false);
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

	const isPayPage = location.pathname.includes(`${campaignId}/payment`);
	const remainingDays = getRemainingDays(companySubclientData?.created_at || '', LIMIT_DAYS_TO_CHANGE_TEMPORARY_PASSWORD);
	const remainingDaysText = `${remainingDays} ${formateNumberQuantityText(remainingDays, ['дня', 'дней', 'дней'])}`;
	const isTemporaryPassword = !!companySubclientData?.meta.is_temporary_password;
	const isWarningVisible = (isTemporaryPassword && remainingDays >= 0);

	const getProductData = async (): Promise<void> => {
		setIsLoading(true);
		try {
			await dispatch(fetchProduct(productId)).unwrap();
		} catch (err) {
			console.log(err);
		}
		setIsLoading(false);
	};

	const getCampaignRights = async (): Promise<void> => {
		try {
			await dispatch(fetchCompanyRights({
				companyId: activeCompanyId,
				subclientId: companySubclientData?.id || ''
			})).unwrap();
		} catch (err) {
			console.log(err);
		}
	};

	useEffect(() => {
		getProductData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (activeCompanyId && companySubclientData && campaignId) {
			getCampaignRights();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeCompanyId, campaignId, companySubclientData]);

	const handleBack = () => {
		const paths = location.pathname.split('/');
		const isCreatePage = location.pathname.includes('create');
		const isEmptyCampaigns = (activeProduct?.advert_campaigns_count || 0) < 1;
		const isProductListRedirect = isCreatePage && isEmptyCampaigns;
		const stepsBack = isProductListRedirect ? 2 : 1;
		const backUrl = paths.slice(0, paths.length - stepsBack).join('/');
		navigate(backUrl);
	};

	const handleSetPasswordChanged = () => {
		setIsConfirmModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsConfirmModalOpen(false);
	};

	const handleConfirmPasswordChanged = async () => {
		setIsSetPasswordChangedLoading(true);
		try {
			await yandexSetPasswordChanged({
				company_id: activeCompanyId,
				subclient_id: companySubclientData?.id || '',
				is_temporary: !isTemporaryPassword
			});
			await dispatch(refreshCompanyData());
			handleCloseModal();
		} catch (e) {
			console.log(e)
		}
		setIsSetPasswordChangedLoading(false);
	};

	return (
		<div className={classes.advertisingCampaignsLayout}>
			{!isPayPage && (
				<div className={classes.backButton} onClick={handleBack}>
					<ArrowIcon className={classes.backButtonIcon} />
					Назад
				</div>
			)}
			{isWarningVisible && (
				<Card className={classes.warning}>
					<WarningIcon className={classes.warningIcon} />
					<div className={classes.warningText}>
						При регистрации в Яндекс.Бизнес вашей учетной записи был назначен временный пароль. Во избежание блокировки учетной записи Яндекс вам необходимо сменить пароль в течение {remainingDaysText}
					</div>
					<div className={classes.warningButtons}>
						<a
							href="https://yandex.ru/business/priority"
							target="_blank"
							rel="noreferrer"
							className={classes.warningButton}
						>
							Сменить пароль
						</a>
						<Button
							className={classes.passwordButton}
							text="Я уже сменил пароль"
							onClick={handleSetPasswordChanged}
							styleType='secondary'
						/>
					</div>
				</Card>
			)}
			{campaignId && !hasCompanyRights && (
				<Card className={classes.warning}>
					<WarningIcon className={classes.warningIcon} />
					<div className={classes.warningText}>
						Вы не сможете запустить рекламную кампанию, так как у вас нет прав на организацию. Чтобы добавить права на организацию перейдите на страницу организации в Яндекс.Бизнес и следуйте инструкциям
					</div>
					<a
						href={`https://yandex.ru/sprav/verification/byPermalink/${companyProductId}`}
						target="_blank"
						rel="noreferrer"
						className={classes.warningButton}
					>
						Яндекс.Бизнес
					</a>
				</Card>
			)}
			{!isLoading && <Outlet />}
			<ModalConfirm
				className={classes.modal}
				isOpen={isConfirmModalOpen}
				onClose={handleCloseModal}
				onConfirm={handleConfirmPasswordChanged}
				text="Вы действительно хотите больше не показывать это сообщение?"
				isLoading={isSetPasswordChangedLoading}
			/>
		</div>
	);
};

export default AdvertisingCampaignsLayout;
